/**
 * governedAegisCall — AEGIS-GOV-0 client-side wrapper.
 *
 * KTRUST-L1: After successful call completion, attestation is emitted via
 * attestationService. Attestation failure does NOT roll back the AEGIS call —
 * KTRUST-L1 resilience principle: operations succeed independently of attestation.
 *
 * Generic entry point for ARGUS and any service that invokes the base AI
 * governance middleware directly (not via a domain-specific wrapper like
 * governedArtemisCall or governedExecCall).
 *
 * Routes to the `governedCall` Cloud Function which:
 *   • enforces ISO 42001 / EU AI Act decision logging
 *   • writes an immutable aegis_decisions record
 *   • samples reasoning chains into aegis_reasoning_chains
 *   • gates HITL approval when confidence < AUTO_SEND_FLOOR (0.85)
 *   • applies meta-d' discrimination threshold (COG-META-1)
 *
 * WRAPPER-DISCIPLINE-EXT-1: disciplineEnforcer applied as final step before
 * return. enforceOutput verdict appended to result as enforcerVerdict.
 * Abstention/silence → finalDisposition='enforcer_blocked'. On isHighStakes,
 * kcritGateway.review() fires and result appended as kcritVerdict.
 *
 * Callers must NOT import @anthropic-ai/sdk directly. This wrapper is the
 * only legitimate path for services outside src/services/artemis/ and
 * src/services/exec/.
 *
 * Three operating modes:
 *   1. CF_URL configured & reachable → server-side governed call.
 *   2. CF_URL configured but unreachable → local deterministic stub, mocked=true.
 *   3. CF_URL not configured (dev / no env) → stub, mocked=true.
 */

import enforceOutput from '../cognition/disciplineEnforcer.js';
// AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: pre-call enforcement contract
import {
  enforceModelTier,
  enforceTokenBudget,
  enforceSystemPrompt,
  enforceCallerRegistration,
  TOKEN_BUDGET,
} from '../cognition/disciplineEnforcer.js';
import { review as kcritReview } from '../cognition/kcritGateway.js';
import { DEFAULT_FORBIDDEN_HEDGES, resolveHedgeList } from '../cognition/forbiddenHedges.js';
import { detectHedge } from '../cognition/hedgeDetector.js';
import attestationService, { SUBSTRATE_ORIGIN } from '../ktrust/attestationService.js';
// AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: audit trail per-tenant
import DataService from '../DataService.js';
// AEGIS-7: presentation contract validator (calibrated-confidence + reasoning-trace + dignity-gate).
// Doctrine: docs/doctrine/AEGIS-PRESENTATION-CONTRACT-DOCTRINE-1.md
import { validatePresentationContract, applyDignityGate } from './aegis7PresentationContract.js';
// AEGIS-8: provenance chain — every governed call attaches prompt-hash + retrieval sources
// + reasoning-trace pointer + confidence calibration so reasoning is end-to-end traceable.
import { buildProvenance } from './provenance/provenanceBuilder.js';
// DEMO-1-v2: demo mode canned-response intercept (additive, default-off for non-demo tenants)
import aegisDemoGuardrail from '../demo/aegisDemoGuardrail.js';
// WRAPPER-DISCIPLINE-EXT-1: registry integrity check at module load (warn-only; CI is the hard gate)
import { checkRegistryIntegrity } from '../governance/wrapperDisciplineEnforcer.js';
// KBRAIN-LOG-1: observation-grounded supervision corpus logging
import kbrainObservationService from '../kbrain/kbrainObservationService.js';
// AGENT-CALIB-1: closed-loop confidence calibration (fire-and-forget; mirrors KTRUST-L1 resilience pattern).
import { recordPrediction as agentCalibRecordPrediction } from '../cognition/agentCalibrationService.js';
// COST-1: per-tenant cost telemetry
import { withCostTracking } from '../cost/withCostTracking.js';
// KTRADECRAFT-IMPL-1: optional vertical tradecraft context injection
import { injectTradecraftContext } from '../ktradecraft/index.js';
import { DEMO_TENANT_ID } from '../../data/tenantDefaults.js';
// SENTINEL-CORE-1: agent identity tagging + semantic lineage + trust lattice (F-SENTINEL-IDENTITY-001)
import { sentinelTag } from '../sentinel/sentinelService.js';
import { auth } from '../../firebase/config.js';
// INTEL-AEGIS-CA-4: post-generation grounding violation detector (F-AI-ATTR-GROUNDED-001 substrate)
import { detectGroundingViolations } from './groundingViolationDetector.js';
// INTEL-AEGIS-CA-8: discipline enforcer telemetry (fire-and-forget; never blocks verdict)
import { recordVerdict as recordDisciplineVerdict } from '../cognition/disciplineTelemetry.js';
// IOT-AEGIS-PROVIDER-ROUTING-1: 5-way provider router hook (additive; activated per-call or globally).
// When params._useProviderRouter is true, the router selects the provider and executes the call.
// Router + observability are fire-and-forget-safe; failures fall through to the CF path.
let _providerRouter = null;
let _aegisObservability = null;
export function configureProviderRouter(router, observability = null) {
  _providerRouter = router;
  _aegisObservability = observability;
}

// ─── AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: Circuit breaker ───────────────────
// Five consecutive CF failures → open circuit; all subsequent calls return stub
// with circuitBreakerOpen=true until the window resets (60 s).
const _circuit = { consecutiveFailures: 0, openUntil: 0 };
const CIRCUIT_FAILURE_THRESHOLD = 5;
const CIRCUIT_RESET_MS = 60_000;

function _circuitIsOpen() {
  if (_circuit.openUntil > Date.now()) return true;
  if (_circuit.openUntil > 0) {
    _circuit.consecutiveFailures = 0;
    _circuit.openUntil = 0;
  }
  return false;
}
function _circuitRecordFailure() {
  _circuit.consecutiveFailures += 1;
  if (_circuit.consecutiveFailures >= CIRCUIT_FAILURE_THRESHOLD) {
    _circuit.openUntil = Date.now() + CIRCUIT_RESET_MS;
    console.warn('[governedAegisCall] Circuit breaker OPEN — 5 consecutive CF failures. Reset in 60 s.');
  }
}
function _circuitRecordSuccess() {
  _circuit.consecutiveFailures = 0;
  _circuit.openUntil = 0;
}

// ─── AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: Retry with exponential backoff ────
// Rate-limit (429) → retry up to 3 times with 1 s / 2 s / 4 s delays.
// Other errors pass through immediately.
async function _withRetry(fn, { maxRetries = 3, baseDelayMs = 1000 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const isRateLimit = err?.status === 429 || err?.message?.includes('429') || err?.code === 'RATE_LIMITED';
      if (!isRateLimit || attempt >= maxRetries) throw err;
      const delay = baseDelayMs * Math.pow(2, attempt);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

// ─── AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: Audit trail emit ──────────────────
// Fire-and-forget write to aegis_call_audit per-tenant collection.
// Failure never blocks the governed call (KTRUST-L1 resilience principle).
function _emitCallAudit(params, { inputTokens, outputTokens, latencyMs, success, errorCode }) {
  if (!params?.tenantId) return;
  try {
    const ds = new DataService(params.tenantId);
    ds.create('aegis_call_audit', {
      tenantId:    params.tenantId,
      module:      params.module || 'unknown',
      sessionId:   params.sessionId || params.correlationId || null,
      model:       params.modelId || null,
      inputTokens:  inputTokens  ?? 0,
      outputTokens: outputTokens ?? 0,
      callPurpose: params.decisionType || params.callPurpose || null,
      latencyMs:   Math.round(latencyMs),
      success,
      errorCode:   errorCode || null,
      timestamp:   new Date().toISOString(),
      deletedAt:   null,
    }).catch(err => console.warn('[governedAegisCall] audit emit failed (non-fatal):', err?.message));
  } catch {
    // Best-effort — never propagate.
  }
}

{
  const _integrityCheck = checkRegistryIntegrity();
  if (!_integrityCheck.passed) {
    console.warn('[governedAegisCall] Wrapper registry integrity warnings:', _integrityCheck.violations.length, _integrityCheck.violations);
  }
}

const CF_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_AEGIS_GOVERNED_CALL_URL) || null;

// Fetch the Firebase ID token for the current user, returning null when no user
// is signed in or Firebase is unconfigured. Used to populate Authorization headers
// on CF requests — the CF verifies this token server-side via admin.auth().verifyIdToken().
async function _getIdToken() {
  try {
    if (auth?.currentUser) return await auth.currentUser.getIdToken();
  } catch { /* token fetch failure is non-blocking */ }
  return null;
}

const REQUIRED_FIELDS = ['agentId', 'module', 'decisionType', 'tenantId'];

function validateParams(params) {
  const missing = REQUIRED_FIELDS.filter(f => params?.[f] == null || params[f] === '');
  if (missing.length > 0) {
    const err = new Error(`governedAegisCall: missing required fields: ${missing.join(', ')}`);
    err.code = 'AEGIS_MISSING_FIELDS';
    throw err;
  }
}

function _stub(params) {
  const decisionId = `dec_aegis_local_${Date.now().toString(36)}`;
  return {
    decisionId,
    status: 'complete',
    mocked: true,
    hitlStatus: 'autonomous',
    requiresApproval: false,
    confidence: 0.75,
    metaD: null,
    text: null,
    outputSummary: {},
    // AEGIS-7 presentation contract — stub responses must conform.
    reasoningTrace: { decisionIdRef: decisionId },
    dignityVerdict: { passed: true, violations: [] },
    agentId: params.agentId,
    module: params.module,
    decisionType: params.decisionType,
  };
}

// AEGIS-7: fire-and-forget event emission on contract violation.
// Uses dynamic import so test environments mocking EventBusService still resolve.
// Failure to publish never alters the response object — contract violation is
// already attached as `contractVerdict` for audit reconstruction.
async function _emitContractViolation(response, params, violations) {
  try {
    const mod = await import('../EventBusService.js');
    const EventBusService = mod.default || mod.EventBusService || mod;
    if (!EventBusService || typeof EventBusService.publish !== 'function') return;
    await EventBusService.publish({
      eventType: 'aegis.contract.violated',
      tenantId: params.tenantId,
      sourceModule: 'system',
      sourceId: response.decisionId || 'unknown',
      payload: {
        decisionId: response.decisionId || null,
        agentId: params.agentId,
        module: params.module,
        decisionType: params.decisionType,
        materialityBand: params?.context?.materialityBand || 'ROUTINE',
        violations,
        priorFinalDisposition: response.finalDisposition,
      },
      actor: { id: params.agentId, role: 'aegis_service' },
    });
  } catch {
    // Best-effort emission — never block the caller.
  }
}

// AEGIS-8: fire-and-forget provenance emission for ARGUS triage / chain inspection.
// Provenance is also attached to the response object — emission is the streaming
// path, the response object is the synchronous audit trail.
async function _emitProvenance(response, params, provenance) {
  try {
    const mod = await import('../EventBusService.js');
    const EventBusService = mod.default || mod.EventBusService || mod;
    if (!EventBusService || typeof EventBusService.publish !== 'function') return;
    await EventBusService.publish({
      eventType: 'aegis.provenance.emitted',
      tenantId: params.tenantId,
      sourceModule: 'system',
      sourceId: response.decisionId || 'unknown',
      payload: {
        decisionId: response.decisionId || null,
        agentId: params.agentId,
        module: params.module,
        decisionType: params.decisionType,
        materialityBand: params?.context?.materialityBand || 'ROUTINE',
        finalDisposition: response.finalDisposition,
        provenance,
      },
      actor: { id: params.agentId, role: 'aegis_service' },
    });
  } catch {
    // Best-effort emission — never block the caller.
  }
}

/**
 * Invoke the base AEGIS governance layer.
 *
 * @param {object} params
 * @param {string} params.agentId       - agent identifier (e.g. 'ARGUS-TMS')
 * @param {string} params.module        - module name (e.g. 'argus')
 * @param {string} params.decisionType  - decision type slug (e.g. 'scrutiny_composition')
 * @param {string} params.tenantId      - tenant identifier
 * @param {object} [params.context]     - arbitrary context passed to the model
 * @param {string} [params.modelId]     - override model (default: server-configured)
 * @param {string} [params.prompt]      - explicit prompt (alternative to context)
 * @returns {Promise<object>} AEGIS decision shape
 */
// DEMO-1-v2: extracted live call path — referenced by aegisDemoGuardrail on cache miss.
async function _liveCall(params) {
  // IOT-AEGIS-PROVIDER-ROUTING-1: router hook — fires when router is globally configured or
  // explicitly requested via params._useProviderRouter. Observability wraps the routed call.
  if (_providerRouter && (params._useProviderRouter !== false)) {
    try {
      const routerCallFn = () => _providerRouter.route(params);
      const result = _aegisObservability
        ? await _aegisObservability.instrument(
            { provider: 'router', tenantId: params.tenantId, model: params.modelId || null, decisionType: params.decisionType },
            routerCallFn
          )
        : await routerCallFn();
      return _applyDisciplineEnforcer(result, params);
    } catch (routerErr) {
      if (routerErr?.code === 'AEGIS_ROUTING_EXHAUSTED') {
        return _applyDisciplineEnforcer({ ..._stub(params), status: 'failed', error: routerErr.message, _routingExhausted: true }, params);
      }
      // Non-exhausted router error: fall through to CF path
      console.warn('[governedAegisCall] Router error, falling through to CF path:', routerErr?.message);
    }
  }

  const body = { ...params, governanceRoute: 'aegis_base_v1' };
  let result;
  if (!CF_URL) {
    result = _stub(params);
  } else if (_circuitIsOpen()) {
    result = { ..._stub(params), status: 'failed', error: 'circuit_breaker_open', circuitBreakerOpen: true };
  } else {
    // COST-1: wrap the inner SDK fetch with cost telemetry (additive — does not replace)
    const costOpts = {
      provider: 'anthropic',
      model: params.modelId || null,
      purpose: params.decisionType || 'aegis',
      tenantId: params.tenantId,
      correlationId: params.correlationId || null,
      estimateCostCents: params._estimateCostCents || 0,
    };
    try {
      result = await withCostTracking(costOpts, () =>
        _withRetry(async () => {
          const idToken = await _getIdToken();
          const res = await fetch(CF_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
            },
            body: JSON.stringify(body),
          });
          // Content policy rejection — graceful degradation; never surface raw API error.
          if (res.status === 451 || res.status === 400) {
            const errBody = await res.json().catch(() => ({}));
            if (errBody?.errorCode === 'content_policy_violation' || errBody?.type === 'error') {
              const incidentId = `cp_${Date.now().toString(36)}`;
              _circuitRecordSuccess();
              return {
                ..._stub(params),
                status: 'degraded',
                error: 'content_policy',
                incidentId,
                text: null,
                outputSummary: { degraded: true, incidentId },
                userMessage: 'Unable to generate — contact support',
              };
            }
          }
          if (!res.ok) {
            const retryErr = new Error(`governedAegisCall HTTP ${res.status}`);
            retryErr.status = res.status;
            throw retryErr;
          }
          const r = await res.json();
          _circuitRecordSuccess();
          attestationService.attest({
            tenantId: params.tenantId,
            claim: `AEGIS call completed: module=${params.module}, decisionType=${params.decisionType}, decisionId=${r.decisionId || 'unknown'}`,
            evidenceRefs: r.decisionId ? [`aegis_decisions/${r.decisionId}`] : [],
            actorId: params.agentId,
            actorRole: 'aegis_service',
            substrateOrigin: SUBSTRATE_ORIGIN.AEGIS,
          }).catch(() => {});
          return r;
        })
      );
    } catch (err) {
      if (err.code === 'COST_CAP_EXCEEDED') throw err;
      _circuitRecordFailure();
      result = { ..._stub(params), status: 'failed', error: err.message };
    }
  }

  // INTEL-AEGIS-CA-4: grounding check — only when caller provides a data pack reference
  // and result has text content. Retry up to MAX_RETRIES on violation.
  const ctx = params.context || {};
  const hasDataPack = (Array.isArray(ctx.dataPackRefs) && ctx.dataPackRefs.length > 0)
    || (typeof ctx.dataPackSummary === 'string' && ctx.dataPackSummary.length > 0);
  if (hasDataPack && result?.text && !result?.mocked) {
    result = await _applyGroundingCheck(result, params, ctx, 0);
  }

  return _applyDisciplineEnforcer(result, params);
}

async function _applyGroundingCheck(result, params, ctx, retryDepth) {
  const MAX_GROUNDING_RETRIES = 2;
  const check = await detectGroundingViolations({
    tenantId: params.tenantId,
    agentId: params.agentId,
    module: params.module,
    draft: result.text,
    dataPackRefs: ctx.dataPackRefs || [],
    dataPackSummary: ctx.dataPackSummary || '',
  });

  const augmented = { ...result, groundingCheck: check };

  if (check.grounded || check.mocked) return augmented;

  if (retryDepth >= MAX_GROUNDING_RETRIES) {
    // Exhausted retries — surface violations; caller decides how to proceed
    augmented.groundingViolationsUnresolved = true;
    return augmented;
  }

  // Retry: re-invoke _liveCall with grounding hint appended to prompt
  const retryParams = {
    ...params,
    prompt: params.prompt
      ? `${params.prompt}\n\n[Grounding note: previous response contained ungrounded claims: ${check.violations.map(v => `"${v.span}"`).join(', ')}. Revise to only use facts from the provided data.]`
      : params.prompt,
    _groundingRetryDepth: retryDepth + 1,
    context: { ...ctx, _groundingRetryDepth: retryDepth + 1 },
  };

  // Avoid infinite recursion: grounding check does not recurse into itself
  if (retryParams.decisionType === 'grounding_check') return augmented;

  const retried = await _rawLiveCall(retryParams);
  if (!retried?.text) return augmented;
  return _applyGroundingCheck(retried, retryParams, ctx, retryDepth + 1);
}

// Raw live call without discipline enforcer — used for grounding retry inner loop
async function _rawLiveCall(params) {
  const body = { ...params, governanceRoute: 'aegis_base_v1' };
  if (!CF_URL) return _stub(params);
  try {
    const idToken = await _getIdToken();
    const res = await fetch(CF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return _stub(params);
    return await res.json();
  } catch {
    return _stub(params);
  }
}

/**
 * AGENT-CALIB-1: fire-and-forget confidence calibration recording.
 * Attaches `calibratedConfidence` and `calibrationApplied` fields to the result
 * mutably (await is fast — local calibration-fn cache, no Firestore read on
 * cache hit). The DB write + EventBus publish inside recordPrediction are
 * themselves non-blocking.
 *
 * On mocked/stub results we still record — calibration is a substrate that
 * accumulates over time, and skipping stubs would bias the corpus during dev.
 *
 * Errors are logged but never thrown; calibration loss never blocks the AEGIS
 * call (KTRUST-L1 resilience principle).
 */
async function _attachCalibration(params, result) {
  if (result?.confidence == null) return;
  if (!params.tenantId || !params.agentId || !params.decisionType) return;
  try {
    const calib = await agentCalibRecordPrediction({
      tenantId: params.tenantId,
      agentId: params.agentId,
      decisionId: result.decisionId,
      decisionType: params.decisionType,
      module: params.module,
      predictedConfidence: result.confidence,
    });
    result.calibratedConfidence = calib.calibratedConfidence;
    result.calibrationApplied = calib.calibrationApplied;
    result.calibrationFnVersion = calib.calibrationFnVersion;
  } catch (err) {
    // Non-fatal. Calibration failure must not affect the governed call result.
    console.warn('[governedAegisCall] calibration attach failed (non-fatal):', err?.message);
  }
}

/**
 * KBRAIN-LOG-1: fire-and-forget observation logging helper.
 * Skips mocked/stub calls so the corpus contains only real inference outputs.
 * Errors are caught internally — observation loss never blocks the caller.
 */
function _kbrainLog(params, result) {
  if (result?.mocked) return; // skip stub responses
  const ctx = params.context || {};
  const band = ctx.materialityBand || 'ROUTINE';
  kbrainObservationService.log({
    tenantId:            params.tenantId,
    capabilityNamespace: params.module || 'aegis',
    modelFamily:         'anthropic',
    modelId:             result?.modelId || params.modelId || null,
    inputContext:        params.context || null,
    modelOutput:         result?.outputSummary || null,
    modelOutputRaw:      result?.text || null,
    correlationId:       params.correlationId || null,
    isHighStakes:        band === 'MATERIAL' || band === 'EXISTENTIAL',
    materialityBand:     band,
  }).catch(err => console.warn('[governedAegisCall] kbrain log failed (non-fatal):', err.message));
}

// CRM-PERFORMANCE-BASELINE-1: Haiku keywords per stack rule (Haiku = extraction/classification).
const _HAIKU_KEYWORDS = ['extract', 'classify', 'parse', 'score', 'analyze', 'detect', 'tag', 'categorize', 'infer'];
function _aegisMetricType(decisionType = '') {
  const dt = decisionType.toLowerCase();
  return _HAIKU_KEYWORDS.some((k) => dt.includes(k)) ? 'aegis-haiku' : 'aegis-sonnet';
}

// ─── AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: streaming entry point ─────────────
// When params._streaming === true, return a ReadableStream from the CF response
// body for conversational UI consumers. Audit is emitted as a best-effort
// side-channel; token counts are 0 for streaming (counted server-side).
async function governedAegisCallStreaming(params) {
  validateParams(params);
  enforceCallerRegistration(params.module);
  if (params.modelId) enforceModelTier(params.modelId, params.module);
  if (params.systemPrompt !== undefined) enforceSystemPrompt(params.systemPrompt);
  if (!CF_URL) {
    _emitCallAudit(params, { inputTokens: 0, outputTokens: 0, latencyMs: 0, success: true });
    return { stream: null, mocked: true };
  }
  const _t0 = performance.now();
  try {
    const idToken = await _getIdToken();
    const res = await fetch(CF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Aegis-Stream': '1',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ ...params, governanceRoute: 'aegis_base_v1', _streaming: true }),
    });
    if (!res.ok) throw new Error(`governedAegisCallStreaming HTTP ${res.status}`);
    _emitCallAudit(params, { inputTokens: 0, outputTokens: 0, latencyMs: performance.now() - _t0, success: true });
    return { stream: res.body, mocked: false };
  } catch (err) {
    _emitCallAudit(params, { inputTokens: 0, outputTokens: 0, latencyMs: performance.now() - _t0, success: false, errorCode: err.code || 'stream_error' });
    return { stream: null, mocked: true, error: err.message };
  }
}

async function governedAegisCall(params) {
  // AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: streaming branch
  if (params._streaming) return governedAegisCallStreaming(params);

  validateParams(params);

  // AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: pre-call enforcement contract
  enforceCallerRegistration(params.module);
  if (params.modelId) {
    enforceModelTier(params.modelId, params.module);
    enforceTokenBudget(params.modelId, params.maxTokens);
  }
  if (params.systemPrompt !== undefined) enforceSystemPrompt(params.systemPrompt);

  const _t0 = performance.now();
  const _metricType = _aegisMetricType(params.decisionType);

  // CRM-PERFORMANCE-BASELINE-1 — record AEGIS latency after call completes (fire-and-forget).
  const _recordAegisPerf = () => {
    try {
      const elapsed = performance.now() - _t0;
      import('../PerformanceService.js').then(({ default: PS }) => {
        PS.recordMetric(_metricType, elapsed, PS.PERSONAS.UNKNOWN, params.module || '').catch(() => {});
      }).catch(() => {});
    } catch {
      // never propagate
    }
  };

  // KTRADECRAFT-IMPL-1: if tradecraftContext flag is set, inject active tradecraft modules for the
  // tenant's verticals into the prompt before governance processing.
  let resolvedParams = params;
  if (params.tradecraftContext && params.tenantId && params.tenantId !== 'kairos-demo' && params.tenantId !== DEMO_TENANT_ID) {
    try {
      const tenant = params._tradecraftVerticalTags ?? [];
      if (tenant.length > 0 && params.prompt) {
        const augmented = await injectTradecraftContext({
          tenantId: params.tenantId,
          verticalTags: tenant,
          aegisPrompt: params.prompt,
        });
        resolvedParams = { ...params, prompt: augmented };
      }
    } catch (_err) {
      // tradecraft injection is additive; failure never blocks the governed call
    }
  }

  // DEMO-1-v2: intercept in demo mode — serve from canned cache when tenantId === 'kairos-demo'.
  if (aegisDemoGuardrail.shouldIntercept(resolvedParams)) {
    const result = await aegisDemoGuardrail.interceptIfDemo(resolvedParams, _liveCall);
    await _attachCalibration(resolvedParams, result);
    _kbrainLog(resolvedParams, result);
    // SENTINEL-CORE-1: tag agent identity + lineage + trust (fire-and-forget; KTRUST-L1)
    sentinelTag({ tenantId: resolvedParams.tenantId, agentId: resolvedParams.agentId, callType: 'aegis', module: resolvedParams.module, decisionType: resolvedParams.decisionType, inputParams: resolvedParams, result, userId: resolvedParams.userId || null })
      .catch(err => console.warn('[governedAegisCall] sentinel tag failed (non-fatal):', err?.message));
    _recordAegisPerf();
    // AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: audit trail (demo path)
    _emitCallAudit(resolvedParams, {
      inputTokens:  result?.inputTokens  ?? 0,
      outputTokens: result?.outputTokens ?? 0,
      latencyMs:    performance.now() - _t0,
      success:      result?.status !== 'failed',
      errorCode:    result?.status === 'failed' ? (result?.error || 'demo_error') : null,
    });
    return result;
  }

  const result = await _liveCall(resolvedParams);
  await _attachCalibration(resolvedParams, result);
  _kbrainLog(resolvedParams, result);
  // SENTINEL-CORE-1: tag agent identity + lineage + trust (fire-and-forget; KTRUST-L1)
  sentinelTag({ tenantId: resolvedParams.tenantId, agentId: resolvedParams.agentId, callType: 'aegis', module: resolvedParams.module, decisionType: resolvedParams.decisionType, inputParams: resolvedParams, result, userId: resolvedParams.userId || null })
    .catch(err => console.warn('[governedAegisCall] sentinel tag failed (non-fatal):', err?.message));
  _recordAegisPerf();
  // AEGIS-GOVERNED-WRAPPER-WORLD-CLASS-1: audit trail (live path)
  _emitCallAudit(resolvedParams, {
    inputTokens:  result?.inputTokens  ?? 0,
    outputTokens: result?.outputTokens ?? 0,
    latencyMs:    performance.now() - _t0,
    success:      result?.status !== 'failed',
    errorCode:    result?.status === 'failed' ? (result?.error || 'unknown') : null,
  });
  return result;
}

async function _applyDisciplineEnforcer(result, params) {
  const context = params.context || {};
  const isDeclarative = context.isDeclarative ?? true;
  const materialityBand = context.materialityBand || 'ROUTINE';
  const isHighStakesCall = materialityBand === 'MATERIAL' || materialityBand === 'EXISTENTIAL';

  // Pre-gate: forbidden-hedge rejection in declarative contexts
  if (isDeclarative && result.text) {
    const hedgeList = resolveHedgeList(params._hedgeConfig || null);
    const { hedgeDetected, matchedPhrases } = detectHedge(result.text, hedgeList);
    if (hedgeDetected) {
      const dignityVerdict = result.dignityVerdict ?? applyDignityGate(result.text);
      const reasoningTrace = result.reasoningTrace
        ?? (result.decisionId ? { decisionIdRef: result.decisionId } : null);
      const blocked = {
        ...result,
        enforcerVerdict: {
          returnType: 'abstention',
          reason: 'forbidden_hedge_in_declarative',
          trippedGate: 'hedge_pre_gate',
          gateResults: [],
          enforcerVersion: 'discipline-enforcer-v1.0',
          gateRegistryVersion: 'gate-registry-v1.0',
        },
        kcritVerdict: null,
        finalDisposition: 'enforcer_blocked',
        dignityVerdict,
        reasoningTrace,
        _hedgeMatchedPhrases: matchedPhrases,
      };
      blocked.contractVerdict = validatePresentationContract(blocked, params);
      // AEGIS-8 provenance — attached even on early-exit so ARGUS can trace.
      blocked.provenance = await buildProvenance(params, blocked);
      void _emitProvenance(blocked, params, blocked.provenance);
      return blocked;
    }
  }

  const envelope = {
    tenantId: params.tenantId,
    module: params.module,
    decisionType: params.decisionType,
    modelId: result.modelId || params.modelId || null,
    rawOutput: result.text || '',
    parsedOutput: result.outputSummary || null,
    confidence: result.confidence ?? null,
    context: {
      isDeclarative,
      materialityBand,
      isHighStakes: isHighStakesCall,
      decisionClass: context.decisionClass || params.decisionType,
      freshnessWindowMs: context.freshnessWindowMs,
      sourceTimestamps: context.sourceTimestamps,
      bayesianPriorRef: context.bayesianPriorRef,
      effortBudget: context.effortBudget,
    },
  };

  const enforcerVerdict = enforceOutput(envelope, params._gateConfig || {});

  // INTEL-AEGIS-CA-8: fire-and-forget telemetry — never blocks the verdict
  recordDisciplineVerdict({
    tenantId: params.tenantId,
    agentId: params.agentId,
    module: params.module,
    decisionType: params.decisionType,
    returnType: enforcerVerdict.returnType,
    trippedGate: enforcerVerdict.trippedGate,
    enforcerVersion: enforcerVerdict.enforcerVersion,
  });

  let kcritVerdict = null;
  if (isHighStakesCall) {
    kcritVerdict = await kcritReview(envelope);
  }

  const isBlocked = enforcerVerdict.returnType !== 'stated_position';
  const kcritBlocked = kcritVerdict && (kcritVerdict.verdict === 'flag' || kcritVerdict.verdict === 'reject');

  let finalDisposition = result.status === 'failed' ? 'failed' : 'completed';
  if (kcritBlocked) finalDisposition = 'kcrit_blocked';
  if (isBlocked) finalDisposition = 'enforcer_blocked';

  // AEGIS-7 presentation contract — fill in dignityVerdict + reasoningTrace defaults,
  // then validate the contract. Doctrine: AEGIS-PRESENTATION-CONTRACT-DOCTRINE-1.
  const dignityVerdict = result.dignityVerdict ?? applyDignityGate(result.text);
  const reasoningTrace = result.reasoningTrace
    ?? (result.decisionId ? { decisionIdRef: result.decisionId } : null);

  const candidate = {
    ...result,
    enforcerVerdict,
    kcritVerdict,
    finalDisposition,
    dignityVerdict,
    reasoningTrace,
  };

  const contractVerdict = validatePresentationContract(candidate, params);
  candidate.contractVerdict = contractVerdict;

  if (!contractVerdict.passed) {
    // Only flip disposition when the call would otherwise have completed —
    // never overwrite an earlier-stage rejection (failed / enforcer_blocked / kcrit_blocked).
    if (finalDisposition === 'completed') {
      candidate.finalDisposition = 'contract_violation';
    }
    // Fire-and-forget event emission for ARGUS triage. Never await result.
    void _emitContractViolation(candidate, params, contractVerdict.violations);
  }

  // AEGIS-8 provenance — attached to every response (completed, blocked, or
  // contract-violated). Provenance is queryable from ARGUS via
  // `aegis_decisions/{decisionId}.provenance` and via the
  // `aegis.provenance.emitted` event below.
  candidate.provenance = await buildProvenance(params, candidate);
  void _emitProvenance(candidate, params, candidate.provenance);

  return candidate;
}

export default governedAegisCall;
export { governedAegisCall, governedAegisCallStreaming };
export { REQUIRED_FIELDS as GOVERNED_AEGIS_REQUIRED_FIELDS };